import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtTime, fmtTimeRange } from "@/lib/format";
import { Printer, ClipboardList, AlertTriangle, CalendarDays, Users as UsersIcon, Star, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const TABS = [
  { id: "attendance", label: "Attendance", icon: ClipboardList },
  { id: "conflicts", label: "Conflicts", icon: AlertTriangle },
  { id: "schedule", label: "Rehearsal Schedule", icon: CalendarDays },
  { id: "cast", label: "Cast List", icon: UsersIcon },
  { id: "auditions", label: "Auditions", icon: Star },
];

export default function Reports() {
  const { production } = useAuth();
  const [tab, setTab] = useState("attendance");
  const [rehearsals, setRehearsals] = useState([]);
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [chars, setChars] = useState([]);
  const [auditionStats, setAuditionStats] = useState(null);
  const [auditionRows, setAuditionRows] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/rehearsals"),
      api.get("/users"),
      api.get("/attendance"),
      api.get("/conflicts"),
      api.get("/characters"),
    ]).then(([r, u, a, c, ch]) => {
      setRehearsals(r.data); setUsers(u.data); setAttendance(a.data);
      setConflicts(c.data); setChars(ch.data);
    });
    // audition endpoints are admin/staff-only — this page is already gated to those roles
    Promise.all([api.get("/auditions/stats"), api.get("/auditions")])
      .then(([s, a]) => { setAuditionStats(s.data); setAuditionRows(a.data); })
      .catch(() => { /* pre-audition productions may 404 */ });
  }, []);

  const castUsers = useMemo(() => users.filter((u) => u.role === "cast"), [users]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const rehearsalMap = useMemo(() => Object.fromEntries(rehearsals.map((r) => [r.id, r])), [rehearsals]);

  // Attendance matrix: rows=cast users, cols=rehearsals, cell=status
  const attMatrix = useMemo(() => {
    const m = {};
    for (const a of attendance) { (m[a.user_id] ||= {})[a.rehearsal_id] = a.status; }
    return m;
  }, [attendance]);
  const attRates = useMemo(() => {
    return castUsers.map((u) => {
      const row = attMatrix[u.id] || {};
      const total = Object.keys(row).length;
      const present = Object.values(row).filter((s) => s === "present" || s === "late").length;
      return { user: u, total, present, rate: total ? present / total : 0 };
    });
  }, [castUsers, attMatrix]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: white !important; color: black !important; }
        .print-area { background: white !important; color: black !important; }
        .print-area * { color: black !important; border-color: #ccc !important; background: transparent !important; }
        .print-area .brand { color: #B45309 !important; }
      }`}</style>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Insights</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Reports</h1>
          <p className="mt-1 text-zinc-400 text-sm">Attendance, conflicts, printable schedule, and cast list.</p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <button
            data-testid="print-btn"
            onClick={() => window.print()}
            className="h-10 px-4 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            data-testid="csv-export-btn"
            onClick={async () => {
              const map = { attendance: "attendance", conflicts: "conflicts", schedule: null, cast: "cast-list", auditions: "auditions" };
              const key = map[tab];
              if (!key) return;
              try {
                const res = await api.get(`/exports/${key}.csv`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const link = document.createElement("a");
                link.href = url; link.download = `${key}.csv`;
                document.body.appendChild(link); link.click();
                setTimeout(() => { URL.revokeObjectURL(url); link.remove(); }, 1000);
              } catch { /* toast import unused here */ }
            }}
            disabled={tab === "schedule"}
            className="h-10 px-4 rounded-md font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
            style={goldBtn}
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-1 no-print border border-white/10 rounded-md bg-[#121212] p-1 w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`report-tab-${id}`}
            onClick={() => setTab(id)}
            className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors
              ${tab === id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="print-area rounded-lg border border-white/10 bg-[#121212] p-6">
        <div className="mb-4 pb-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 brand">StageCall</div>
          <h2 className="mt-1 font-display text-2xl">{production?.title || "Production"} — {TABS.find((t) => t.id === tab)?.label}</h2>
          <div className="text-xs text-zinc-500 mt-1">Generated {fmtDate(new Date())} · {rehearsals.length} rehearsals · {castUsers.length} cast</div>
        </div>

        {tab === "attendance" && (
          <div data-testid="report-attendance">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {attRates.map((r) => (
                <div key={r.user.id} className="rounded border border-white/10 p-3 text-sm flex items-center gap-3">
                  <Avatar user={r.user} size="w-9 h-9" refresh={r.user.avatar_path || 0} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{r.user.name}</div>
                    <div className="text-xs text-zinc-500">{r.user.character || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{Math.round(r.rate * 100)}%</div>
                    <div className="text-[10px] text-zinc-500">{r.present}/{r.total}</div>
                  </div>
                </div>
              ))}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px]">
                  <th className="py-2">Cast Member</th>
                  {rehearsals.slice(0, 12).map((r) => (
                    <th key={r.id} className="py-2 px-1 text-center">{fmtDate(r.date).slice(0, 5)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {castUsers.map((u) => (
                  <tr key={u.id} className="border-t border-white/5">
                    <td className="py-2 pr-2 whitespace-nowrap">{u.name}</td>
                    {rehearsals.slice(0, 12).map((r) => {
                      const s = attMatrix[u.id]?.[r.id];
                      const glyph = s === "present" ? "✓" : s === "late" ? "L" : s === "excused" ? "E" : s === "absent" ? "✗" : "·";
                      const color = s === "present" ? "text-emerald-400" : s === "late" ? "text-amber-400" : s === "excused" ? "text-blue-400" : s === "absent" ? "text-red-400" : "text-zinc-600";
                      return <td key={r.id} className={`text-center py-2 font-mono ${color}`}>{glyph}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-[10px] text-zinc-500">Legend: ✓ present · L late · E excused · ✗ absent · · not recorded</div>
          </div>
        )}

        {tab === "conflicts" && (
          <div data-testid="report-conflicts">
            {conflicts.length === 0 && <div className="text-sm text-zinc-500">No conflicts on file.</div>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px] border-b border-white/10">
                  <th className="py-2">Cast Member</th><th className="py-2">Date</th><th className="py-2">Time</th><th className="py-2">Reason</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((c) => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="py-2 pr-3">{c.user_name}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{fmtDate(c.date)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{fmtTimeRange(c.start_time, c.end_time)}</td>
                    <td className="py-2 pr-3 text-zinc-300">{c.reason}</td>
                    <td className="py-2 uppercase text-[10px] tracking-widest">{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "schedule" && (
          <div data-testid="report-schedule">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px] border-b border-white/10">
                  <th className="py-2">Date</th><th className="py-2">Time</th><th className="py-2">Title</th><th className="py-2">Location</th>
                </tr>
              </thead>
              <tbody>
                {rehearsals.map((r) => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="py-2 pr-3 font-mono text-xs">{fmtDate(r.date)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{fmtTimeRange(r.start_time, r.end_time)}</td>
                    <td className="py-2 pr-3">{r.title}</td>
                    <td className="py-2 text-zinc-400">{r.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "cast" && (
          <div data-testid="report-cast">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {chars.length > 0 ? chars.map((c) => {
                const actor = c.actor_user_id ? userMap[c.actor_user_id] : null;
                return (
                  <div key={c.id} className="rounded border border-white/10 p-3 flex items-center gap-3">
                    <Avatar user={actor || { name: c.name }} size="w-11 h-11" refresh={actor?.avatar_path || 0} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display">{c.name}{c.voice_type && <span className="text-xs text-zinc-500 ml-2">· {c.voice_type}</span>}</div>
                      <div className="text-xs text-zinc-400">{actor ? actor.name : "Unassigned"}</div>
                      {actor && <div className="text-[11px] text-zinc-500">{actor.email}{actor.phone ? ` · ${actor.phone}` : ""}</div>}
                    </div>
                  </div>
                );
              }) : castUsers.map((u) => (
                <div key={u.id} className="rounded border border-white/10 p-3 flex items-center gap-3">
                  <Avatar user={u} size="w-11 h-11" refresh={u.avatar_path || 0} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display">{u.character || u.name}</div>
                    <div className="text-xs text-zinc-400">{u.name}</div>
                    <div className="text-[11px] text-zinc-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "auditions" && (
          <div data-testid="report-auditions">
            {!auditionStats ? (
              <div className="text-sm text-zinc-500">No audition data yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <StatBox label="Submissions" value={auditionStats.total} />
                  <StatBox label="Pending" value={auditionStats.by_status.pending || 0} />
                  <StatBox label="Cast" value={auditionStats.by_status.cast || 0} accent="text-emerald-400" />
                  <StatBox label="Do not cast" value={auditionStats.by_status.reject || 0} accent="text-red-400" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                  <StatBox label="Score submissions" value={auditionStats.score_submissions} />
                  <StatBox label="Adjudicators" value={auditionStats.unique_scorers} />
                  <StatBox label="Finalized" value={auditionStats.finalized} accent="text-gold" />
                </div>
                {auditionStats.top_roles?.length > 0 && (
                  <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Most requested roles</div>
                    <div className="flex flex-wrap gap-2">
                      {auditionStats.top_roles.map((r) => (
                        <span key={r.role} className="text-xs px-2 py-1 rounded border border-white/10 bg-[#0A0A0A] text-zinc-200">
                          {r.role} <span className="text-gold font-mono ml-1">×{r.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">All submissions</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px] border-b border-white/10">
                        <th className="py-2">Name</th><th className="py-2">Email</th><th className="py-2 text-center">Acting</th><th className="py-2 text-center">Vocal</th><th className="py-2 text-center">Dance</th><th className="py-2 text-center">Overall</th><th className="py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditionRows.map((a) => (
                        <tr key={a.id} className="border-b border-white/5">
                          <td className="py-2 pr-3">{a.name}{a.cast_character && <span className="text-[11px] text-zinc-500 ml-1">→ {a.cast_character}</span>}</td>
                          <td className="py-2 pr-3 text-zinc-400 text-xs">{a.email}</td>
                          <td className="py-2 text-center font-mono text-xs">{a.acting_avg ?? "—"}</td>
                          <td className="py-2 text-center font-mono text-xs">{a.vocal_avg ?? "—"}</td>
                          <td className="py-2 text-center font-mono text-xs">{a.dance_avg ?? "—"}</td>
                          <td className="py-2 text-center font-mono text-xs text-gold">{a.overall_avg ?? "—"}</td>
                          <td className="py-2 uppercase text-[10px] tracking-widest">{a.status || "pending"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-3xl ${accent}`}>{value}</div>
    </div>
  );
}
